import { useCallback, useEffect } from 'react';
import type { MarkRequired } from 'ts-essentials';

import type { BaseOptions, CompatibleStorageValue } from './types';
import { pruneStorage, setValueForKey } from './utils';

type WriteOptions<
  T extends CompatibleStorageValue,
  PersistedParsedValue extends CompatibleStorageValue,
> = MarkRequired<
  Pick<
    BaseOptions<T, PersistedParsedValue>,
    'duration' | 'stringify' | 'version' | 'readOnly' | 'transformValueOnWrite'
  >,
  'version' | 'readOnly' | 'transformValueOnWrite'
> & {
  whenRef: React.MutableRefObject<boolean>;
};

export const useWriteToLocalStorage = <
  T extends CompatibleStorageValue,
  PersistedParsedValue extends CompatibleStorageValue = Partial<T>,
>(
  unversionedKey: string,
  storedValue: T,
  {
    stringify,
    whenRef,
    readOnly = false,
    version,
    duration,
    transformValueOnWrite,
  }: WriteOptions<T, PersistedParsedValue>,
) => {
  const write = useCallback(
    (value: T) => {
      pruneStorage();

      if (readOnly) return;
      if (!whenRef.current) {
        return;
      }

      const transformedValue = transformValueOnWrite(value);
      setValueForKey({ unversionedKey, version, value: transformedValue, duration, stringify });
    },
    [unversionedKey, readOnly, stringify, transformValueOnWrite, version, duration, whenRef],
  );

  useEffect(() => {
    write(storedValue);
  }, [write, storedValue]);

  return write;
};
